import { Injectable, computed, inject } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Observable, map, of, tap } from 'rxjs';
import { BrandingService, KundeTokenService } from '@comparit/infrastructure';
import { TaskContextService } from '@comparit/shared';
import { KundeLoginResponse } from '@comparit/common-api';
import { KfzTaskData } from '../interfaces/generated';
import { toKundeDisplayInfo } from '../helpers/kunde-display-info';

export interface LoginDialogData {
  /** Task the customer is bound to, when one is already known. */
  taskId: string | null;
  /**
   * Token from a restore link (`/laden?token=…`). The backend prefers it over
   * `taskId` and it is the only way to bind to an already-claimed calculation,
   * whose restore answers 401 without revealing the taskId.
   */
  taskToken: string | null;
  /** Snapshot of the in-progress calculation, for a guest that has no taskId yet. */
  taskData: KfzTaskData | null;
  /**
   * `'gated'` means a caller is blocked waiting on this login — a task restore
   * on `/laden`, or a request the interceptor is about to retry. The dialog must
   * hand the response back as soon as login succeeds instead of showing its
   * success screen, whose "Zum Kundenportal" link would navigate the customer
   * away from what they were doing. Every brand login dialog has to honour this.
   */
  mode: 'default' | 'gated';
  reason?: 'rueckruf';
}

/**
 * Opens the active brand's login dialog and applies the two effects that are
 * safe on any route: the customer token and the taskId.
 *
 * Deliberately free of form state. `/laden` depends on this service and runs
 * *before* the session is hydrated from a restored task; constructing
 * `FormsService` there would cache empty forms, and its write-back sync would
 * then overwrite the restored calculation — in sessionStorage and, via
 * `KfzTaskSyncService`, on the server. Every form-aware concern therefore lives
 * one layer up, in `FunnelLoginService`.
 */
@Injectable({ providedIn: 'root' })
export class KundeLoginDialogService {
  private readonly dialog = inject(MatDialog);
  private readonly brandingService = inject(BrandingService);
  private readonly kundeToken = inject(KundeTokenService);
  private readonly taskContext = inject(TaskContextService);

  /** Whether the active brand ships a login dialog at all. */
  readonly available = computed(() => !!this.brandingService.config()?.loginDialogComponent);

  /**
   * Opens the dialog and emits its result once: the response on success, or
   * null when it was dismissed or the brand has no login dialog configured.
   *
   * Subscribe exactly once — the token/taskId effects run on subscription.
   */
  open(data: LoginDialogData): Observable<KundeLoginResponse | null> {
    const dialogComponent = this.brandingService.config()?.loginDialogComponent;
    if (!dialogComponent) {
      console.warn('KundeLoginDialogService: no loginDialogComponent configured for brand');
      return of(null);
    }

    return this.dialog
      .open<unknown, LoginDialogData, KundeLoginResponse | null>(dialogComponent, { data })
      .afterClosed()
      .pipe(
        map(response => response ?? null),
        tap(response => {
          if (response) this.applyLogin(response);
        })
      );
  }

  applyLogin(response: KundeLoginResponse): void {
    this.kundeToken.setToken(
      response.token,
      response.gueltigBis,
      toKundeDisplayInfo(response.stammdaten)
    );
    this.taskContext.setTaskId(response.taskId);
  }
}
